import React, { useState, useEffect } from 'react';
import { app, auth } from './firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, query, where, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';

const db = getFirestore(app);

export default function ClientPortalView({ theme, toggleTheme }) {
  const [user, setUser] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  
  // Login State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  // Dashboard Data
  const [timesheets, setTimesheets] = useState([]);
  const [activeConsultants, setActiveConsultants] = useState([]);
  const [portalUsers, setPortalUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // --- 1. AUTH & METADATA LISTENER ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
            // Force refresh to get latest claims
            const tokenResult = await currentUser.getIdTokenResult(true);
            const claims = tokenResult.claims;

            // SECURITY CHECK 1: If Employee, Redirect to Admin Dashboard
            if (claims.role === 'internal') {
                window.location.href = '/'; 
                return;
            }

            // SECURITY CHECK 2: Must be Client
            if (claims.role !== 'client') {
                console.warn("Unauthorized access attempt (Non-Client).");
                await signOut(auth);
                alert("Access Denied: This area is for Clients only.");
                setUser(null);
                setAuthLoading(false);
                return;
            }

            setUser(currentUser);
            
            const cid = claims.customerId;
            if (cid) {
                setCustomerId(cid);
                // Fetch Company Details (Name, etc.)
                const custDoc = await getDoc(doc(db, "customers", cid));
                if (custDoc.exists()) {
                    setCustomerData(custDoc.data());
                }
            }
        } catch (error) {
            console.error("Error fetching client claims:", error);
            await signOut(auth);
            setUser(null);
        }
      } else {
        setUser(null);
        setCustomerId(null);
        setCustomerData(null);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  // --- 2. DATA FETCHING ---
  const fetchDashboardData = async () => {
      if (!user || !customerData) return;
      setLoading(true);
      
      try {
          // A. Fetch Active Contracts (Consultants working for this client)
          const contractsQ = query(
              collection(db, "active_contracts"), 
              where("customerId", "==", customerId),
              where("status", "==", "Active")
          );
          const contractsSnap = await getDocs(contractsQ);
          const contracts = contractsSnap.docs.map(d => ({id: d.id, ...d.data()}));
          setActiveConsultants(contracts);

          // B. Fetch Pending Timesheets
          // Fetch 'Submitted' timesheets for this customer
          const tsQ = query(
              collection(db, "timesheets"), 
              where("status", "==", "Submitted"),
              where("customerId", "==", customerId)
          );
          const tsSnap = await getDocs(tsQ);
          setTimesheets(tsSnap.docs.map(d => ({id: d.id, ...d.data()})));

          // C. Fetch Active Portal Users (Colleagues)
          if (customerId) {
              const usersQ = query(collection(db, "customers", customerId, "portal_users"));
              const usersSnap = await getDocs(usersQ);
              setPortalUsers(usersSnap.docs.map(d => d.data()));
          }

      } catch (err) { 
          console.error("Error fetching dashboard data:", err); 
      } finally { 
          setLoading(false); 
      }
  };

  useEffect(() => {
      if(user && customerData) fetchDashboardData();
  }, [user, customerData]);

  const [error, setError] = useState('');

  // --- HANDLERS ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try { 
        await signInWithEmailAndPassword(auth, email, password); 
    } catch (err) { 
        console.error(err);
        setError("Invalid login credentials.");
    }
  };

  const handleApproval = async (id, newStatus) => {
      if(!window.confirm(`Mark timesheet as ${newStatus}?`)) return;
      try {
          await updateDoc(doc(db, "timesheets", id), { 
              status: newStatus,
              updatedAt: new Date().toISOString(),
              approvedBy: user.email 
          });
          // Optimistic update
          setTimesheets(prev => prev.filter(t => t.id !== id));
      } catch (err) {
          alert("Error updating status.");
      }
  };

  // --- RENDER: LOADING ---
  if (authLoading) {
      return (
          <div className="d-flex align-items-center justify-content-center vh-100 bg-body">
              <div className="text-main">Loading Portal...</div>
          </div>
      );
  }

  // --- RENDER: LOGIN ---
  if (!user) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100 bg-transparent-theme">
        <div className="card bg-card border-theme shadow-lg p-4" style={{maxWidth: '400px', width: '100%'}}>
          <div className="text-center mb-4">
            <h2 className="fw-bold text-main">Nettic<span className="text-accent">.</span></h2>
            <p className="text-muted-theme small">Client Portal Login</p>
          </div>
          
          {error && (
            <div className="alert alert-danger py-2 small d-flex align-items-center">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="mb-3">
                <label className="form-label small text-muted-theme">Email</label>
                <input 
                    className="form-control" 
                    type="email" 
                    value={email} 
                    onChange={e=>setEmail(e.target.value)} 
                    required 
                />
            </div>
            <div className="mb-4">
                <label className="form-label small text-muted-theme">Password</label>
                <input 
                    className="form-control" 
                    type="password" 
                    value={password} 
                    onChange={e=>setPassword(e.target.value)} 
                    required 
                />
            </div>
            <button className="btn btn-primary w-100">Login</button>
          </form>
          <div className="mt-3 text-center">
            <small className="text-muted-theme">Authorized access only. Contact Nettic for support.</small>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDER: DASHBOARD ---
  return (
    <div className="min-vh-100 bg-body text-main">
      {/* NAVBAR */}
      <nav className="navbar border-bottom border-theme px-4 shadow-sm bg-card">
        <div className="d-flex align-items-center">
            <span className="navbar-brand mb-0 h1 fw-bold text-main">Nettic<span className="text-accent">.</span></span>
            <span className="border-start border-secondary mx-3 ps-3 text-muted-theme fw-bold">
                {customerData ? customerData.companyName : 'Loading...'}
            </span>
        </div>
        <div className="d-flex align-items-center gap-3">
            <span className="text-main small d-none d-md-inline">{user.email}</span>
            <button className="btn btn-sm btn-outline-secondary border-theme" onClick={toggleTheme} title="Toggle Theme">
                {theme === 'dark' ? <i className="bi bi-moon-stars-fill"></i> : <i className="bi bi-sun-fill"></i>}
            </button>
            <button className="btn btn-outline-danger btn-sm" onClick={() => signOut(auth)}>Logout</button>
        </div>
      </nav>

      <div className="container py-5">
        
        {/* ROW 1: HEADER & REFRESH */}
        <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
                <h4 className="fw-bold text-main mb-0">Dashboard</h4>
                <p className="text-muted-theme mb-0 small">Overview of active consultants and pending actions.</p>
            </div>
            <button className="btn btn-sm btn-outline-primary" onClick={fetchDashboardData}>
                <i className="bi bi-arrow-clockwise me-1"></i> Refresh Data
            </button>
        </div>

        {loading ? <div className="text-center py-5 text-muted-theme">Loading data...</div> : (
            <>
                {/* SECTION 1: PENDING TIMESHEETS */}
                <h5 className="text-main mb-3 border-bottom border-theme pb-2">Pending Approvals</h5>
                <div className="row g-4 mb-5">
                    {timesheets.length > 0 ? timesheets.map(t => (
                        <div key={t.id} className="col-md-6 col-lg-4">
                            <div className="card h-100 bg-card border-theme shadow-sm">
                                <div className="card-header bg-transparent border-theme d-flex justify-content-between align-items-center">
                                    <h5 className="card-title text-main mb-0 fw-bold">{t.consultantName}</h5>
                                    <span className="badge bg-warning text-dark">Pending</span>
                                </div>
                                <div className="card-body d-flex flex-column">
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-muted-theme">Period:</span>
                                            <span className="text-main fw-bold">{t.month} {t.year}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-muted-theme">Total Hours:</span>
                                            <span className="text-main fw-bold">{t.totalHours}</span>
                                        </div>
                                    </div>
                                    {t.proofUrl ? (
                                        <a href={t.proofUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-info mb-3">
                                            <i className="bi bi-file-earmark-pdf me-2"></i> View PDF
                                        </a>
                                    ) : (
                                        <div className="alert alert-secondary py-2 small mb-3 fst-italic">No PDF attached.</div>
                                    )}
                                    <div className="mt-auto pt-3 border-top border-theme d-flex gap-2">
                                        <button className="btn btn-success flex-grow-1" onClick={() => handleApproval(t.id, 'Approved')}>Approve</button>
                                        <button className="btn btn-outline-danger flex-grow-1" onClick={() => handleApproval(t.id, 'Rejected')}>Reject</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )) : (
                        <div className="col-12">
                            <div className="alert alert-success bg-success bg-opacity-10 border-success text-success d-flex align-items-center">
                                <i className="bi bi-check-circle-fill me-2"></i> All caught up! No pending timesheets.
                            </div>
                        </div>
                    )}
                </div>

                <div className="row g-4">
                    {/* SECTION 2: ACTIVE CONSULTANTS */}
                    <div className="col-lg-8">
                        <h5 className="text-main mb-3 border-bottom border-theme pb-2">Active Consultants</h5>
                        <div className="card bg-card border-theme shadow-sm">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0 text-main align-middle">
                                    <thead className="bg-input">
                                        <tr>
                                            <th className="bg-card border-theme">Name</th>
                                            <th className="bg-card border-theme">Role</th>
                                            <th className="bg-card border-theme">Contract Start</th>
                                            <th className="bg-card border-theme">Contract End</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeConsultants.length > 0 ? activeConsultants.map(c => (
                                            <tr key={c.id}>
                                                <td className="bg-card border-theme fw-bold">{c.consultantName}</td>
                                                <td className="bg-card border-theme">{c.jobTitle}</td>
                                                <td className="bg-card border-theme text-muted-theme small">{c.startDate}</td>
                                                <td className="bg-card border-theme text-muted-theme small">{c.endDate}</td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="4" className="text-center py-4 text-muted-theme">No active consultants found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 3: AUTHORIZED USERS */}
                    <div className="col-lg-4">
                        <h5 className="text-main mb-3 border-bottom border-theme pb-2">Portal Access</h5>
                        <div className="card bg-card border-theme shadow-sm">
                            <div className="card-body p-0">
                                <ul className="list-group list-group-flush">
                                    {portalUsers.length > 0 ? portalUsers.map((u, i) => (
                                        <li key={i} className="list-group-item bg-transparent border-theme d-flex align-items-center">
                                            <div className="rounded-circle bg-secondary bg-opacity-25 text-main d-flex align-items-center justify-content-center me-3" style={{width: '35px', height: '35px'}}>
                                                {u.name ? u.name.charAt(0).toUpperCase() : '?'}
                                            </div>
                                            <div>
                                                <div className="fw-bold text-main">{u.name}</div>
                                                <div className="small text-muted-theme">{u.email}</div>
                                            </div>
                                        </li>
                                    )) : (
                                        <li className="list-group-item bg-transparent text-muted-theme py-3 text-center">No other users found.</li>
                                    )}
                                </ul>
                            </div>
                            <div className="card-footer bg-input border-theme">
                                <small className="text-muted-theme">Contact Nettic to manage access.</small>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )}
      </div>
    </div>
  );
}